// src/Components/Forms/Financement/Penalite/Penalite.tsx

import { useState, useEffect, useMemo, useCallback } from 'react';
import { Tabs, Space, Button, Form, Spin, Tag, Input, Card, Statistic, Tooltip, Divider } from 'antd';
import Table from '../../../../TableResizable';
import type { TabsProps } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  UserOutlined,
  ReloadOutlined,
  CheckOutlined,
  CloseOutlined,
  EyeOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import { Box, Typography, Grid, Paper } from '@mui/material';

import Tab1 from './Tab1';
import Utilisateur from '../../../../../Models/Utilisateur';
import { useAuthHeader, useAuthUser } from '../../../../../Services/Auth/sessionAuth';
import Data from '../../../../../Data/Data.json';
import SweetAlert from 'sweetalert2';
import { Crypter } from '../../../../../Functions/Security/Crypter';
import { PenaliteDto } from '../../../../../Models/PenaliteDto';
import Params from '../../../../../Types/ParamsPicklist';

type PenaliteRow = PenaliteDto & {
  key: React.Key;
};

interface PenaliteGroup {
  key: string;
  NumeroFinancement: string;
  CodeFinancement: string;
  Numcli?: string;
  Nomcli?: string;
  children: PenaliteRow[];
  totalPenalites: number;
  montantTotal: number;
  montantImputeTotal: number;
}

const Penalite = () => {
  const auth = useAuthUser();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));
  const authHeader = useAuthHeader();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [penalites, setPenalites] = useState<PenaliteDto[]>([]);
  const [filteredData, setFilteredData] = useState<PenaliteDto[]>([]);
  const [groupedData, setGroupedData] = useState<PenaliteGroup[]>([]);

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedPenalite, setSelectedPenalite] = useState<PenaliteDto | null>(null);
  const [detailData, setDetailData] = useState<any>(null);

  const [activeTab, setActiveTab] = useState('1');
  const [searchText, setSearchText] = useState('');

  /**
   * Retourne le montant constaté.
   */
  const getMontantConstate = useCallback(
    (penalite: PenaliteDto): number => {
      return Number(penalite.MontantPenalite ?? 0);
    },
    [],
  );

  const getMontantImpute = useCallback(
    (penalite: PenaliteDto): number => Number(penalite.MontantProposeImputation ?? 0),
    [],
  );

  /**
   * Formatage des montants à l'entier avec espacement des milliers et sans devise.
   */
  const formatMontant = useCallback((value: number): string => {
    return Number(value ?? 0).toLocaleString('fr-FR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });
  }, []);

  /**
   * Chargement initial des pénalités.
   */
  const loadPenalites = useCallback(() => {
    setLoading(true);

    const searchParams: Params = {
      CodeAgence: '1',
      Table: 'ViewAnyPenaliteDetail',
      Colonne1: 'PenaliteID',
      Colonne2: 'PenaliteID',
      Init: '',
      Colagence: '1',
      Choix: 37,
      colfiltre: 'Statut',
      valfiltre: 'EN_ATTENTE_VALIDATION',
    };

    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };

    axios
      .request(config)
      .then((response) => {
        setLoading(false);
        if (!response.data) return;

        const res = JSON.parse(Crypter.decrypt(response.data)).Value;

        if (res.StatutCode === 200) {
          const datas: PenaliteDto[] = res.Result?.Table || [];

          setPenalites(datas);
          setFilteredData(datas);
        } else {
          SweetAlert.fire({
            title: res.Message,
            icon: 'warning',
          });
        }
      })
      .catch((error) => {
        setLoading(false);
        SweetAlert.fire({
          title: 'Erreur',
          text: error?.message || String(error),
          icon: 'error',
        });
      });
  }, [authHeader]);

  useEffect(() => {
    loadPenalites();
  }, [loadPenalites]);

  /**
   * Recherche globale.
   */
  useEffect(() => {
    let data = penalites;

    if (searchText.trim()) {
      const lower = searchText.toLowerCase().trim();

      data = data.filter(
        (p) =>
          p.NumeroFinancement?.toLowerCase().includes(lower) ||
          p.CodeFinancement?.toLowerCase().includes(lower) ||
          p.Numcli?.toLowerCase().includes(lower) ||
          p.Nomcli?.toLowerCase().includes(lower) ||
          p.Statut?.toLowerCase().includes(lower) ||
          p.PenaliteID?.toString().includes(lower) ||
          p.NumeroEcheance?.toString().includes(lower),
      );
    }

    setFilteredData(data);
  }, [searchText, penalites]);

  /**
   * Grouper les pénalités par numéro de financement.
   */
  useEffect(() => {
    const groups: Record<string, PenaliteRow[]> = {};

    filteredData.forEach((penalite) => {
      const key = penalite.NumeroFinancement || 'SANS_FINANCEMENT';

      if (!groups[key]) {
        groups[key] = [];
      }

      groups[key].push({
        ...penalite,
        key: penalite.PenaliteID ?? `${key}_${groups[key].length}`,
      });
    });

    const grouped: PenaliteGroup[] = Object.keys(groups).map((key) => {
      const items = groups[key];
      const first = items[0];

      return {
        key: `grp_${key}`,
        NumeroFinancement: key,
        CodeFinancement: first?.CodeFinancement || '',
        Numcli: first?.Numcli || '',
        Nomcli: first?.Nomcli || '',
        children: items,
        totalPenalites: items.length,

        montantTotal: items.reduce(
          (sum, penalite) => sum + getMontantConstate(penalite),
          0,
        ),

        montantImputeTotal: items.reduce(
          (sum, penalite) => sum + getMontantImpute(penalite),
          0,
        ),
      };
    });

    setGroupedData(grouped);
  }, [filteredData, getMontantConstate, getMontantImpute]);

  /**
   * Totaux globaux.
   */
  const totals = useMemo(() => {
    const montantConstate = filteredData.reduce(
      (sum, penalite) => sum + getMontantConstate(penalite),
      0,
    );

    const montantImpute = filteredData.reduce(
      (sum, penalite) => sum + getMontantImpute(penalite),
      0,
    );

    return {
      nombre: filteredData.length,
      montantConstate,
      montantImpute,
      ecart: montantConstate - montantImpute,
    };
  }, [filteredData, getMontantConstate, getMontantImpute]);

  /**
   * Récupère les pénalités réellement sélectionnées.
   */
  const selectedPenalites = useMemo(() => {
    const selectedIds = new Set(
      selectedRowKeys
        .filter(
          (key) => !(typeof key === 'string' && key.startsWith('grp_')),
        )
        .map((key) => String(key)),
    );

    return penalites.filter((penalite) =>
      selectedIds.has(String(penalite.PenaliteID)),
    );
  }, [penalites, selectedRowKeys]);

  /**
   * Validation des montants sélectionnés avant action groupée.
   */
  const validateSelectedMontants = useCallback(() => {
    for (const penalite of selectedPenalites) {
      const montantConstate = getMontantConstate(penalite);
      const montantImpute = getMontantImpute(penalite);

      if (montantImpute < 0) {
        return {
          valid: false,
          message: `Le montant imputé de la pénalité #${penalite.PenaliteID} est négatif.`,
        };
      }

      if (montantImpute > montantConstate) {
        return {
          valid: false,
          message: `Le montant imputé de la pénalité #${penalite.PenaliteID} dépasse le montant constaté.`,
        };
      }
    }

    return {
      valid: true,
      message: '',
    };
  }, [getMontantConstate, getMontantImpute, selectedPenalites]);

  /**
   * Colonnes du tableau.
   */
  const columns: ColumnsType<PenaliteGroup | PenaliteRow> = [
    {
      title: 'Pénalité',
      dataIndex: 'PenaliteID',
      key: 'PenaliteID',
      width: 100,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <Tag color="blue">{record.totalPenalites} pénalité(s)</Tag>;
        }
        return text;
      },
    },
    {
      title: 'Type financement',
      dataIndex: 'CodeFinancement',
      key: 'CodeFinancement',
      width: 140,
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <strong>{record.CodeFinancement || '-'}</strong>;
        }
        return text || '-';
      },
    },
    {
      title: 'N° Financement',
      dataIndex: 'NumeroFinancement',
      key: 'NumeroFinancement',
      width: 160,
    },
    {
      title: 'Client',
      dataIndex: 'Nomcli',
      key: 'Nomcli',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <strong>{text || record.Numcli || '-'}</strong>;
        }
        return text || record.Numcli || '-';
      },
    },
    {
      title: 'N° Échéance',
      dataIndex: 'NumeroEcheance',
      key: 'NumeroEcheance',
      width: 110,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return <Tag color="cyan">{record.totalPenalites} échéance(s)</Tag>;
        }
        return text || '-';
      },
    },
    {
      title: 'Date constat',
      dataIndex: 'DateConstat',
      key: 'DateConstat',
      width: 120,
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) return '';
        return text ? new Date(text).toLocaleDateString('fr-FR') : '';
      },
    },
    {
      title: 'Jours retard',
      dataIndex: 'JoursRetard',
      key: 'JoursRetard',
      width: 100,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) return '';
        return text ?? '-';
      },
    },
    {
      title: 'Montant constaté',
      dataIndex: 'MontantPenalite',
      key: 'MontantPenalite',
      width: 160,
      align: 'right',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          return (
            <Tooltip title="Total des montants réellement constatés en base">
              <strong>{formatMontant(record.montantTotal)}</strong>
            </Tooltip>
          );
        }

        return (
          <Typography component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatMontant(Number(text ?? 0))}
          </Typography>
        );
      },
    },
    {
      title: 'Montant imputé',
      key: 'MontantImpute',
      width: 190,
      align: 'right',
      render: (_, record) => {
        if ('children' in record && Array.isArray(record.children)) {
          const difference = record.montantTotal - record.montantImputeTotal;

          return (
            <div>
              <Tooltip title="Total des montants décidés par le comptable">
                <strong>{formatMontant(record.montantImputeTotal)}</strong>
              </Tooltip>

              {difference !== 0 && (
                <div style={{ marginTop: 4, fontSize: 11, color: '#d48806' }}>
                  Écart : {formatMontant(difference)}
                </div>
              )}
            </div>
          );
        }

        const penalite = record as PenaliteRow;
        return (
          <Typography component="span" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
            {formatMontant(getMontantImpute(penalite))}
          </Typography>
        );
      },
    },
    {
      title: 'Statut',
      dataIndex: 'Statut',
      key: 'Statut',
      width: 120,
      align: 'center',
      render: (text, record) => {
        if ('children' in record && Array.isArray(record.children)) return '';

        return (
          <Tag
            color={
              text === 'CONSTATE'
                ? 'blue'
                : text === 'ENCAISSE'
                ? 'green'
                : text === 'ANNULE'
                ? 'red'
                : 'default'
            }
          >
            {text || 'N/A'}
          </Tag>
        );
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 110,
      fixed: 'right',
      align: 'center',
      render: (_, record) => {
        if ('children' in record && Array.isArray(record.children)) return null;

        const penalite = record as PenaliteRow;

        return (
          <Button
            size="small"
            icon={<EyeOutlined />}
            onClick={() => handleSelectPenalite(penalite)}
          >
            Détails
          </Button>
        );
      },
    },
  ];

  /**
   * Sélection d'une pénalité.
   */
  const handleSelectPenalite = (penalite: PenaliteDto) => {
    form.setFieldsValue({
      ...penalite,
      Nomcli: penalite.Nomcli || '',
    });
    setSelectedPenalite(penalite);

    if (penalite.PenaliteID != null) {
      loadDetail(penalite.PenaliteID);
    }

    setActiveTab('2');
  };

  /**
   * Chargement des détails.
   */
  const loadDetail = (penaliteId: number) => {
    setLoading(true);

    const searchParams: Params = {
      CodeAgence: '',
      Table: 'ViewAnyPenaliteDetail',
      Colonne1: 'PenaliteID',
      Colonne2: '',
      Init: penaliteId.toString(),
      Colagence: '',
      Choix: 35,
    };

    const config = {
      method: 'get',
      url: Data.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        Authorization: authHeader(),
        CypherSearch: Crypter.encrypt(JSON.stringify(searchParams)),
      },
    };

    axios
      .request(config)
      .then((response) => {
        setLoading(false);

        if (!response.data) return;

        const res = JSON.parse(Crypter.decrypt(response.data)).Value;

        if (res.StatutCode === 200) {
          const detail = res.Result?.Table?.[0] ?? null;

          if (!detail) {
            SweetAlert.fire({ title: 'Aucun détail trouvé', icon: 'warning' });
            return;
          }

          setDetailData(detail);
          form.setFieldsValue({
            PenaliteID: detail.PenaliteID,
            CodeFinancement: detail.CodeFinancement,
            NumeroFinancement: detail.NumeroFinancement,
            Nomcli: detail.Nomcli || detail.NomClient || '',
            NumeroEcheance: detail.NumeroEcheance,
            DateConstat: detail.DateConstat,
            JoursRetard: detail.JoursRetard,
            BaseCalcul: detail.BaseCalcul,
            TauxPenalite: detail.TauxPenalite,
            MontantPenalite: detail.MontantPenalite,
            MontantEncaisse: detail.MontantEncaisse,
            SoldePenalite: detail.SoldePenalite,
            CompteCreancePenalite: detail.CompteCreancePenalite,
            CompteCharite: detail.CompteCharite,
            CompteSourceClient: detail.CompteSourceClient,
            RefConstat: detail.RefConstat,
            RefEncaissement: detail.RefEncaissement,
            RefAnnulation: detail.RefAnnulation,
            Statut: detail.Statut,
            DateEncaissement: detail.DateEncaissement,
            MotifAnnulation: detail.MotifAnnulation,
          });
        } else {
          SweetAlert.fire({
            title: res.Message,
            icon: 'warning',
          });
        }
      })
      .catch((error) => {
        setLoading(false);

        SweetAlert.fire({
          title: 'Erreur',
          text: error?.message || String(error),
          icon: 'error',
        });
      });
  };

  /**
   * Actions groupées.
   */
  const handleBulkAction = (action: string) => {
    if (selectedPenalites.length === 0) {
      SweetAlert.fire({
        title: 'Aucune pénalité sélectionnée',
        text: 'Veuillez sélectionner au moins une pénalité.',
        icon: 'warning',
      });
      return;
    }

    const validation = validateSelectedMontants();

    if (!validation.valid) {
      SweetAlert.fire({
        title: 'Montant invalide',
        text: validation.message,
        icon: 'warning',
      });
      return;
    }

    SweetAlert.fire({
      title: `Confirmer l'action "${action}"`,
      html: `
        <div style="text-align:left">
          <p><strong>${selectedPenalites.length}</strong> pénalité(s) sélectionnée(s).</p>
          <p>Montant constaté :
             <strong>${formatMontant(
               selectedPenalites.reduce(
                 (sum, penalite) => sum + getMontantConstate(penalite),
                 0,
               ),
             )}</strong>
          </p>
          <p>Montant imputé :
             <strong>${formatMontant(
               selectedPenalites.reduce(
                 (sum, penalite) => sum + getMontantImpute(penalite),
                 0,
               ),
             )}</strong>
          </p>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: 'Confirmer',
      cancelButtonText: 'Annuler',
      icon: 'question',
    }).then((res) => {
      if (!res.isConfirmed) return;

      setLoading(true);

      const selectedIds = selectedPenalites
        .map((penalite) => Number(penalite.PenaliteID))
        .filter((id) => !Number.isNaN(id));

      const montants = selectedPenalites.map((penalite) => ({
        PenaliteID: penalite.PenaliteID,
        MontantConstate: getMontantConstate(penalite),
        MontantImpute: getMontantImpute(penalite),
      }));

      const payload = {
        Action: action,
        PenaliteIds: selectedIds,
        Montants: montants,
        Utilisateur: user.coduti,
      };

      const config = {
        method: 'post',
        url: Data.ApiUrl.ValidatePenalite,
        headers: {
          Authorization: authHeader(),
          'Content-Type': 'application/json',
        },
        data: Crypter.encrypt(JSON.stringify(payload)),
      };

      axios
        .request(config)
        .then((response) => {
          setLoading(false);

          if (!response.data) return;

          const responseValue = JSON.parse(Crypter.decrypt(response.data)).Value;

          if (responseValue.StatutCode === 200) {
            SweetAlert.fire({
              title: 'Succès',
              text: responseValue.Message,
              icon: 'success',
            });

            loadPenalites();
            setSelectedRowKeys([]);
          } else {
            SweetAlert.fire({
              title: responseValue.Message,
              icon: 'warning',
            });
          }
        })
        .catch((error) => {
          setLoading(false);

          SweetAlert.fire({
            title: 'Erreur',
            text: error?.message || String(error),
            icon: 'error',
          });
        });
    });
  };

  /**
   * Configuration de la sélection.
   */
  const rowSelection = {
    selectedRowKeys,
    checkStrictly: false,
    onChange: (keys: React.Key[]) => {
      setSelectedRowKeys(keys);
    },
  };

  /**
   * Nombre de pénalités actuellement sélectionnées.
   */
  const nombreSelection = selectedPenalites.length;

  /**
   * Totaux des lignes sélectionnées.
   */
  const totalsSelection = useMemo(() => {
    return selectedPenalites.reduce(
      (acc, penalite) => {
        acc.constate += getMontantConstate(penalite);
        acc.impute += getMontantImpute(penalite);
        return acc;
      },
      {
        constate: 0,
        impute: 0,
      },
    );
  }, [selectedPenalites, getMontantConstate, getMontantImpute]);

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: <span>Liste des pénalités</span>,
      children: (
        <Paper elevation={2} sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          {/* EN-TÊTE / RECHERCHE */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} md={6}>
              <Input
                placeholder="Rechercher un financement, client, pénalité..."
                allowClear
                size="large"
                style={{ width: '100%' }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                prefix={<SearchOutlined />}
              />
            </Grid>

            <Grid item xs={12} md={6} display="flex" justifyContent="flex-end" gap={1}>
              <Button icon={<ReloadOutlined />} onClick={loadPenalites}>
                Rafraîchir
              </Button>
            </Grid>
          </Grid>

          {/* INDICATEURS GLOBAUX */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Statistic title="Pénalités" value={totals.nombre} />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Statistic
                  title="Montant constaté"
                  value={totals.montantConstate}
                  formatter={(value) =>
                    Number(value).toLocaleString('fr-FR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Statistic
                  title="Montant a imputer"
                  value={totals.montantImpute}
                  valueStyle={{
                    color: totals.ecart === 0 ? undefined : '#d48806',
                  }}
                  formatter={(value) =>
                    Number(value).toLocaleString('fr-FR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Statistic
                  title="Écart"
                  value={totals.ecart}
                  valueStyle={{
                    color: totals.ecart === 0 ? '#389e0d' : '#d48806',
                  }}
                  formatter={(value) =>
                    Number(value).toLocaleString('fr-FR', {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })
                  }
                />
              </Grid>
            </Grid>
          </Card>

          {/* ACTIONS DE SUPERVISION */}
          <Box sx={{ mb: 2 }}>
            <Space wrap size="middle">
              <Button
                type="primary"
                icon={<CheckOutlined />}
                disabled={selectedPenalites.length === 0}
                onClick={() => handleBulkAction('SUPERVISION')}
              >
                Passer en supervision
              </Button>

              <Button
                danger
                icon={<CloseOutlined />}
                disabled={selectedPenalites.length === 0}
                onClick={() => handleBulkAction('REJET')}
              >
                Rejeter
              </Button>

              <Button
                danger
                icon={<CloseOutlined />}
                disabled={selectedPenalites.length === 0}
                onClick={() => handleBulkAction('REJET')}
              >
                Annuler
              </Button>

              {nombreSelection > 0 && (
                <Tag
                  icon={<UserOutlined />}
                  color="blue"
                  style={{ fontSize: 13, padding: '5px 10px' }}
                >
                  {nombreSelection} sélectionnée(s)
                </Tag>
              )}
            </Space>
          </Box>

          {/* RÉSUMÉ DE LA SÉLECTION */}
          {nombreSelection > 0 && (
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space wrap split={<Divider type="vertical" />}>
                <span>
                  <strong>Sélection :</strong> {nombreSelection} pénalité(s)
                </span>

                <span>
                  <strong>Constaté :</strong> {formatMontant(totalsSelection.constate)}
                </span>

                <span>
                  <strong>Imputé :</strong> {formatMontant(totalsSelection.impute)}
                </span>

                <span>
                  <strong>Écart :</strong>{' '}
                  {formatMontant(totalsSelection.constate - totalsSelection.impute)}
                </span>
              </Space>
            </Card>
          )}

          {/* TABLEAU */}
          <Table
            size="small"
            bordered
            rowSelection={rowSelection}
            columns={columns}
            dataSource={groupedData}
            rowKey="key"
            scroll={{ x: 1450, y: 500 }}
            pagination={{
              pageSize: 50,
              showSizeChanger: true,
              pageSizeOptions: ['25', '50', '100', '200'],
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} sur ${total} pénalité(s)`,
            }}
            expandable={{ defaultExpandAllRows: false }}
            rowClassName={(record) => {
              if ('children' in record && Array.isArray(record.children)) {
                return 'penalite-group-row';
              }
              return '';
            }}
          />
        </Paper>
      ),
    },

    {
      key: '2',
      label: <span>Détails de la pénalité</span>,
      children: (
        <Paper elevation={2} sx={{ p: 2, mt: 2, borderRadius: 2 }}>
          {selectedPenalite ? (
            <Tab1 form={form} detail={detailData} loading={loading} />
          ) : (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <Typography color="text.secondary">
                Sélectionnez une pénalité pour afficher ses détails.
              </Typography>
            </Box>
          )}
        </Paper>
      ),
    },
  ];

  return (
    <div className="tab">
      <Spin spinning={loading} size="large" style={{ color: 'green' }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} size="large" type="card" items={items} />
      </Spin>
    </div>
  );
};

export default Penalite;